import { Stack, Typography, IconButton, Box, Button } from "@mui/material";
import React, { useMemo, useRef, useEffect, useState } from "react";
import TextFieldLabel from "./TextFieldLabel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { useField, useFormikContext } from "formik";

export default function FileUpload({
  label,
  requiredMark,
  placeholder,
  name,
  accept,
  multiple = false,
  existingUrl = null,
}) {
  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [formatError, setFormatError] = useState("");

  const files = useMemo(() => {
    if (!field.value) return [];
    return Array.isArray(field.value) ? field.value : [field.value];
  }, [field.value]);

  const previewUrls = useMemo(() => {
    return files.map((file) => {
      if (typeof file === "string") return file;
      return URL.createObjectURL(file);
    });
  }, [files]);

  useEffect(() => {
    return () => {
      previewUrls.forEach((url, index) => {
        const file = files[index];
        if (typeof file !== "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrls, files]);

  // ✅ Validate file type and size (max 5MB)
  const validateFiles = (fileList) => {
    if (!fileList.length) return false;

    const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
    const acceptedTypes = accept ? accept.split(",").map((a) => a.trim()) : [];

    const invalidFiles = [];
    const oversizedFiles = [];

    fileList.forEach((file) => {
      if (accept && !acceptedTypes.some((type) => file.type.match(type))) {
        invalidFiles.push(file.name);
      }
      if (file.size > maxSize) {
        oversizedFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setFormatError(`Invalid file format: ${invalidFiles.join(", ")}`);
      return false;
    }

    if (oversizedFiles.length > 0) {
      setFormatError(`File size exceeds 5 MB: ${oversizedFiles.join(", ")}`);
      return false;
    }

    setFormatError("");
    return true;
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!validateFiles(selectedFiles)) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFieldValue(name, multiple ? selectedFiles : selectedFiles[0]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemoveFile = (index = 0) => {
    if (multiple) {
      const next = [...files];
      next.splice(index, 1);
      setFieldValue(name, next.length ? next : null);
    } else {
      setFieldValue(name, null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length === 0) return;
    if (!validateFiles(droppedFiles)) return;
    setFieldValue(name, multiple ? droppedFiles : droppedFiles[0]);
  };

  const renderFilePreview = (file, url, index) => {
    const getFileType = (f) => {
      if (typeof f === "string") {
        return accept?.startsWith("video/") ? "video/mp4" : "image/jpeg";
      }
      return f.type;
    };

    const fileType = getFileType(file);
    const isImage = fileType.startsWith("image/");
    const isVideo = fileType.startsWith("video/");
    const displayName = typeof file === "string" ? "" : file.name;

    return (
      <Box
        key={index}
        sx={{
          position: "relative",
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 1,
          mb: multiple ? 1 : 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {isImage && (
          <img
            src={url}
            alt={displayName}
            style={{
              width: "100%",
              maxHeight: 180,
              objectFit: "cover",
              borderRadius: 6,
            }}
          />
        )}
        {isVideo && (
          <video
            src={url}
            controls
            style={{ width: "100%", maxHeight: 180, borderRadius: 6 }}
          />
        )}
        <Typography variant="body2" noWrap>
          {displayName}
        </Typography>
        <IconButton
          size="small"
          onClick={() => handleRemoveFile(index)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            bgcolor: "background.paper",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Stack spacing={1}>
      <TextFieldLabel label={label} required={requiredMark} />

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
      />

      {files.length > 0 ? (
        <Box>
          {files.map((file, idx) =>
            renderFilePreview(file, previewUrls[idx], idx)
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => inputRef.current?.click()}
            sx={{ mt: 1 }}
          >
            Replace File
          </Button>
        </Box>
      ) : (
        <Box
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            dragCounter.current += 1;
            setIsDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault();
            dragCounter.current -= 1;
            if (dragCounter.current === 0) {
              setIsDragging(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            dragCounter.current = 0;
            setIsDragging(false);
            const droppedFiles = Array.from(e.dataTransfer.files || []);
            if (droppedFiles.length === 0) return;
            if (!validateFiles(droppedFiles)) return;
            setFieldValue(name, multiple ? droppedFiles : droppedFiles[0]);
          }}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "#ccc",
            borderRadius: 1,
            p: 2,
            minHeight: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            bgcolor: isDragging ? "action.hover" : "transparent",
            transition: "all 0.2s ease",
          }}
        >
          <CloudUploadIcon
            sx={{
              mr: 1,
              color: isDragging ? "primary.main" : "text.secondary",
            }}
          />
          <Typography
            variant="body2"
            color={isDragging ? "primary.main" : "text.secondary"}
          >
            {isDragging
              ? "Drop files here"
              : placeholder || "Click or drag files to upload"}
          </Typography>
        </Box>
      )}

      {/* ✅ Info note */}
      <Typography variant="caption" color="text.secondary">
        Maximum file size: 5 MB
      </Typography>

      {/* ✅ Show format or Formik error */}
      {(formatError || (meta.touched && meta.error)) && (
        <Typography variant="body2" color="error">
          {formatError || meta.error}
        </Typography>
      )}
    </Stack>
  );
}
